/**
 * KB-3593 AC-2 (A) — remember whether this page load STARTED with a persisted
 * pro session, so a session the SDK drops during boot can be told apart from a
 * visitor who was never signed in.
 *
 * At boot `@firebase/auth` reloads the stored user and, on any error other than
 * `auth/network-request-failed`, clears it (`reloadAndSetCurrentUserOrClear`).
 * A revoked refresh token is one such error: on staging (KB-3593 AC-1, executed)
 * `user.reload()` after a revoke fails `auth/user-token-expired` and drops the
 * user; that the boot path clears on the same code is read from the SDK source.
 * The first auth answer is then `null` and the user lands on /login with nothing
 * saying why. Once the SDK has cleared storage the evidence is gone, so it is
 * read here, synchronously, before `initializeAuth` runs.
 *
 * The key is the SDK's own `_persistenceKeyName("authUser", apiKey, appName)`.
 * Only `browserLocalPersistence` and `browserSessionPersistence` can hold a user
 * across a load (`firebaseService.ts` never hands the SDK IndexedDB), so these
 * two stores are the whole population.
 *
 * A storage read that throws answers `false`, and that is ground truth rather
 * than a fallback: a store that cannot be read cannot have restored a session
 * either, so the SDK falls through to in-memory persistence and there is nothing
 * to lose at boot.
 *
 * Dependency-free on purpose. `@/services/firebaseService` is mocked wholesale
 * in the AuthContext render suites, and a named export read through that mock
 * would throw at collection (landmine L379) — so the value lives here instead.
 */

const DEFAULT_APP_NAME = "[DEFAULT]";

let persistedUserAtBoot = false;

export function persistedAuthUserKey(apiKey: string, appName: string = DEFAULT_APP_NAME): string {
  return `firebase:authUser:${apiKey}:${appName}`;
}

function storeHolds(read: () => Storage, key: string): boolean {
  try {
    return read().getItem(key) !== null;
  } catch {
    return false;
  }
}

/** Call once, synchronously, immediately before `initializeAuth`. */
export function captureBootAuthSnapshot(apiKey: string, appName: string = DEFAULT_APP_NAME): void {
  const key = persistedAuthUserKey(apiKey, appName);
  persistedUserAtBoot =
    storeHolds(() => window.localStorage, key) || storeHolds(() => window.sessionStorage, key);
}

export function hadPersistedAuthUserAtBoot(): boolean {
  return persistedUserAtBoot;
}
